import { MessagesPage } from '@/pages/messages/MessagesPage';
import { lazy, type JSX } from 'react';
import { permissionKeys } from '@/utility/permissions';
import { lazyWithPreload } from '@gokrew/ui/utility/lazyWithPreload';
import type { EmployeePermissionScope } from '@gokrew/models/employee-permissions';

// ─── Priority: bundled in main chunk ─────────────────────────────────────────
import DashboardPage from '@/pages/Dashboard/DashboardPage';
import { MyTeamGuard } from '@/pages/my-team/components/MyTeamGuard';
import { PerformanceGuard } from '@/components/PerformanceGuard';
import { LoginPage } from '@/pages/auth/LoginPage';
import { ResetForgotPasswordPage } from '@/pages/auth/ResetForgotPasswordPage';
import { SignCompletePage } from '@/pages/sign/SignCompletePage';
import { SignGatePage } from '@/pages/sign/SignGatePage';
import { LegacyRedirect } from './LegacyRedirect';
import { TrainingGuard } from '@/components/TrainingGuard';

// ─── Lazy: code-split into separate chunks ────────────────────────────────────
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  }))
);

// ─── lazyWithPreload: lazy + preload in one declaration ──────────────────────
const MyInformationPage = lazyWithPreload(
  () => import('@/pages/my-information/MyInformationPage'),
  'MyInformationPage'
);
const PersonalInformationPage = lazyWithPreload(
  () => import('@/pages/personal-information/PersonalInformationPage')
);
const EducationDetailsPage = lazyWithPreload(
  () => import('@/pages/education-details/EducationDetailsPage')
);
const DocumentPage = lazyWithPreload(
  () => import('@/pages/document/DocumentPage'),
  'DocumentPage'
);
const BankingDetailsPage = lazyWithPreload(
  () => import('@/pages/banking-details/BankingDetailsPage')
);
const LeaveManagementPage = lazyWithPreload(
  () => import('@/pages/leave-management/EmployeeLeaveManagementPage'),
  'LeaveManagementPage'
);
const LeaveCalendar = lazyWithPreload(
  () => import('@/pages/leave-management/LeaveCalendar'),
  'LeaveCalendar'
);
const LeaveRequestOverview = lazyWithPreload(
  () => import('@/pages/leave-management/LeaveRequestOverview'),
  'LeaveRequestOverview'
);
const FamilyDetailsPage = lazyWithPreload(
  () => import('@/pages/family/FamilyDetailsPage')
);
const AssetDetailsPage = lazyWithPreload(
  () => import('@/pages/asset-details/AssetDetailsPage')
);
const LeaveTransactionsPage = lazyWithPreload(
  () => import('@/pages/leave-transactions/LeaveTransactionsPage')
);
const FilesPage = lazyWithPreload(() => import('@/pages/files/FilesPage'));
const FolderDetails = lazyWithPreload(
  () => import('@/pages/files/summary/FolderDetails')
);
const FolderDetailsAll = lazyWithPreload(() =>
  import('@/pages/files/summary/FolderDetails').then((m) => ({
    default: function FolderDetailsAll() {
      return <m.default isAll />;
    },
  }))
);
const DevelopmentPage = lazyWithPreload(
  () => import('@/pages/my-information/DevelopmentPage')
);
const NotesPage = lazyWithPreload(() => import('@/pages/notes/NotesPage'));
const EmployeeTasksPage = lazyWithPreload(
  () => import('@/pages/tasks/EmployeeTasksPage'),
  'EmployeeTasksPage'
);
const TimesheetPage = lazyWithPreload(
  () => import('@/pages/timesheet/TimesheetPage')
);
const TimeTracker = lazyWithPreload(
  () => import('@/pages/timesheet/Tracker/TimeTracker')
);
const TimesheetHistoryGuard = lazyWithPreload(
  () => import('@/pages/timesheet/history/TimesheetHistoryGuard'),
  'TimesheetHistoryGuard'
);
const MySurveysPage = lazyWithPreload(
  () => import('@/pages/surveys/MySurveysPage'),
  'MySurveysPage'
);

const MyDocumentsToReadPage = lazyWithPreload(
  () => import('@/pages/signatures/MyDocumentsToReadPage'),
  'MyDocumentsToReadPage'
);
const ReadDocumentPage = lazyWithPreload(
  () => import('@/pages/signatures/ReadDocumentPage'),
  'ReadDocumentPage'
);
const SignatureApprovalPage = lazyWithPreload(
  () => import('@/pages/signatures/ApprovalPage'),
  'ApprovalPage'
);
const SignatureAnswersPage = lazyWithPreload(
  () => import('@/pages/signatures/SignatureAnswersPage'),
  'SignatureAnswersPage'
);
const MyTrainingsPage = lazyWithPreload(
  () => import('@/pages/trainings/MyTrainingsPage'),
  'MyTrainingsPage'
);
const TrainingPlayerPage = lazyWithPreload(
  () => import('@/pages/trainings/TrainingPlayerPage'),
  'TrainingPlayerPage'
);
const TakeSurveyPage = lazyWithPreload(
  () => import('@/pages/surveys/TakeSurveyPage'),
  'TakeSurveyPage'
);

const MyReviewsPage = lazyWithPreload(
  () => import('@/pages/performance/reviews/MyReviewsPage'),
  'MyReviewsPage'
);
const WriteReviewPage = lazyWithPreload(
  () => import('@/pages/performance/reviews/WriteReviewPage'),
  'WriteReviewPage'
);
const SharedReviewPage = lazyWithPreload(
  () => import('@/pages/performance/reviews/SharedReviewPage'),
  'SharedReviewPage'
);
const TeamFeedbackPage = lazyWithPreload(
  () => import('@/pages/performance/team-feedback/TeamFeedbackPage'),
  'TeamFeedbackPage'
);
const OneOnOnesPage = lazyWithPreload(
  () => import('@/pages/performance/one-on-ones/OneOnOnesPage'),
  'OneOnOnesPage'
);
const OneOnOneDetailPage = lazyWithPreload(
  () => import('@/pages/performance/one-on-ones/OneOnOneDetailPage'),
  'OneOnOneDetailPage'
);
const ManagerReviewPage = lazyWithPreload(
  () => import('@/pages/performance/manager-reviews/ManagerReviewPage'),
  'ManagerReviewPage'
);
const GoalsPage = lazyWithPreload(
  () => import('@/pages/performance/goals/GoalsPage'),
  'GoalsPage'
);
const MyTeamPerformance = lazyWithPreload(
  () => import('@/pages/my-team/performance/MyTeamPerformance'),
  'MyTeamPerformance'
);

const MyTeamPage = lazyWithPreload(
  () => import('@/pages/my-team/MyTeamPage'),
  'MyTeamPage'
);
const MyTeamHome = lazyWithPreload(
  () => import('@/pages/my-team/MyTeamHome'),
  'MyTeamHome'
);
const TeamOverview = lazyWithPreload(
  () => import('@/pages/my-team/team/TeamOverview'),
  'TeamOverview'
);
const MyTeamCalendar = lazyWithPreload(
  () => import('@/pages/my-team/calendar/MyTeamCalendar'),
  'MyTeamCalendar'
);
const MyTeamApprovals = lazyWithPreload(
  () => import('@/pages/my-team/approvals/MyTeamApprovals'),
  'MyTeamApprovals'
);
const MyTeamDelegations = lazyWithPreload(
  () => import('@/pages/my-team/delegations/MyTeamDelegations'),
  'MyTeamDelegations'
);
const MemberDetailPage = lazyWithPreload(
  () => import('@/pages/my-team/member/MemberDetailPage'),
  'MemberDetailPage'
);
const MyTeamTrainings = lazyWithPreload(
  () => import('@/pages/my-team/trainings/MyTeamTrainings')
);

const CalendarConnectionPage = lazyWithPreload(
  () => import('@/pages/calendar-connection/CalendarConnectionPage')
);

const CandidateReviewsPage = lazyWithPreload(
  () => import('@/pages/candidate-reviews/CandidateReviewsPage')
);

const SlackNotificationSettingsPage = lazyWithPreload(
  () => import('@/pages/leave-management/SlackNotificationSettingsPage'),
  'SlackNotificationSettingsPage'
);

// ─── Types ────────────────────────────────────────────────────────────────────

interface ChildRoute {
  index?: boolean;
  path?: string;
  element: JSX.Element;
  scope?: EmployeePermissionScope | EmployeePermissionScope[];
  preload?: () => Promise<unknown>;
}

export interface ProtectedRoute {
  path: string;
  element: JSX.Element;
  scope?: EmployeePermissionScope | EmployeePermissionScope[];
  children?: ChildRoute[];
  preload?: () => Promise<unknown>;
}

export interface AuthRoute {
  path: string;
  element: JSX.Element;
}

// ─── Route definitions ────────────────────────────────────────────────────────

export const authRoutes: AuthRoute[] = [
  { path: '/login', element: <LoginPage /> },
  { path: '/forgot-password', element: <ForgotPasswordPage /> },
  { path: '/reset-password', element: <ResetForgotPasswordPage /> },
  // E-signature (D74): every emailed signing link opens this gate first,
  // logged in or not; the signing page sends signers back to /sign/complete.
  { path: '/sign/complete', element: <SignCompletePage /> },
  { path: '/sign/:token', element: <SignGatePage /> },
];

export const protectedRoutes: ProtectedRoute[] = [
  { path: 'messages', element: <MessagesPage /> },
  {
    path: 'dashboard',
    element: <DashboardPage />,
    scope: permissionKeys.dashboard.read,
    // DashboardPage is eagerly bundled — no preload needed
  },
  // My information: the profile and every section HR holds about the
  // employee, one tab each, like HR's employee page. The old per-section
  // URLs below redirect here (emails already sent still link to them).
  {
    path: 'my-information',
    element: <MyInformationPage.component />,
    preload: MyInformationPage.preload,
    children: [
      {
        path: 'notes',
        element: <NotesPage.component />,
        preload: NotesPage.preload,
      },
      {
        path: 'signatures',
        element: <MyDocumentsToReadPage.component />,
        preload: MyDocumentsToReadPage.preload,
      },
      {
        path: 'development',
        element: <DevelopmentPage.component />,
        preload: DevelopmentPage.preload,
      },
      {
        index: true,
        element: <PersonalInformationPage.component />,
        preload: PersonalInformationPage.preload,
      },
      {
        path: 'education',
        element: <EducationDetailsPage.component />,
        preload: EducationDetailsPage.preload,
        scope: permissionKeys.education.read,
      },
      {
        path: 'documents',
        element: <DocumentPage.component />,
        preload: DocumentPage.preload,
        scope: permissionKeys.document.read,
      },
      {
        path: 'family',
        element: <FamilyDetailsPage.component />,
        preload: FamilyDetailsPage.preload,
        scope: permissionKeys.family_detail.read,
      },
      {
        path: 'banking',
        element: <BankingDetailsPage.component />,
        preload: BankingDetailsPage.preload,
        scope: permissionKeys.bank_detail.read,
      },
      {
        path: 'assets',
        element: <AssetDetailsPage.component />,
        preload: AssetDetailsPage.preload,
        scope: permissionKeys.asset.read,
      },
    ],
  },
  {
    path: 'personal-information',
    element: <LegacyRedirect to="/my-information" />,
  },
  {
    path: 'education-details',
    element: <LegacyRedirect to="/my-information/education" />,
  },
  {
    path: 'documents',
    element: <LegacyRedirect to="/my-information/documents" />,
  },
  {
    path: 'banking-details',
    element: <LegacyRedirect to="/my-information/banking" />,
  },
  {
    path: 'family',
    element: <LegacyRedirect to="/my-information/family" />,
  },
  {
    path: 'asset-details',
    element: <LegacyRedirect to="/my-information/assets" />,
  },
  // My team is for every employee (D49): the Team tab (manager, teammates,
  // who's off) needs no guard. People, Team calendar, Out of office and a
  // member's page stay manager-only (MyTeamGuard); Approvals also opens to
  // someone covering a manager's approvals (D10). The server scopes every
  // manager call to the caller's reporting subtree anyway.
  {
    path: 'my-team',
    element: <MyTeamPage.component />,
    preload: MyTeamPage.preload,
    children: [
      {
        // People for a manager, Team for everyone else.
        index: true,
        element: <MyTeamHome.component />,
        preload: MyTeamHome.preload,
      },
      {
        path: 'team',
        element: <TeamOverview.component />,
        preload: TeamOverview.preload,
      },
      {
        path: 'calendar',
        element: (
          <MyTeamGuard>
            <MyTeamCalendar.component />
          </MyTeamGuard>
        ),
        preload: MyTeamCalendar.preload,
      },
      {
        path: 'trainings',
        element: (
          <TrainingGuard>
            <MyTeamGuard>
              <MyTeamTrainings.component />
            </MyTeamGuard>
          </TrainingGuard>
        ),
        preload: MyTeamTrainings.preload,
      },
      {
        path: 'performance',
        element: (
          <PerformanceGuard>
            <MyTeamGuard>
              <MyTeamPerformance.component />
            </MyTeamGuard>
          </PerformanceGuard>
        ),
        preload: MyTeamPerformance.preload,
      },
      {
        path: 'approvals',
        element: (
          <MyTeamGuard allowDelegate>
            <MyTeamApprovals.component />
          </MyTeamGuard>
        ),
        preload: MyTeamApprovals.preload,
      },
      {
        path: 'delegations',
        element: (
          <MyTeamGuard>
            <MyTeamDelegations.component />
          </MyTeamGuard>
        ),
        preload: MyTeamDelegations.preload,
      },
    ],
  },
  {
    path: 'my-team/members/:employeeId',
    element: (
      <MyTeamGuard>
        <MemberDetailPage.component />
      </MyTeamGuard>
    ),
    preload: MemberDetailPage.preload,
  },
  {
    path: 'leave-management',
    element: <LeaveManagementPage.component />,
    preload: LeaveManagementPage.preload,
    scope: permissionKeys.leave.read,
    children: [
      {
        index: true,
        element: <LeaveRequestOverview.component />,
        preload: LeaveRequestOverview.preload,
      },
      {
        path: 'calendar',
        element: <LeaveCalendar.component />,
        preload: LeaveCalendar.preload,
      },
      {
        path: 'slack-notifications',
        element: <SlackNotificationSettingsPage.component />,
        preload: SlackNotificationSettingsPage.preload,
      },
    ],
  },
  {
    path: 'files',
    element: <FilesPage.component />,
    preload: FilesPage.preload,
    scope: permissionKeys.file_management.read,
    children: [
      {
        index: true,
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
      },
      {
        path: 'all',
        element: <FolderDetailsAll.component />,
        preload: FolderDetails.preload,
      },
      {
        path: ':folderId',
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
      },
      {
        path: ':folderId/:subFolderId',
        element: <FolderDetails.component />,
        preload: FolderDetails.preload,
      },
    ],
  },
  {
    path: 'leave-transactions',
    element: <LeaveTransactionsPage.component />,
    preload: LeaveTransactionsPage.preload,
    scope: permissionKeys.leave.read,
  },
  {
    path: 'timesheet',
    element: <TimesheetPage.component />,
    preload: TimesheetPage.preload,
    scope: permissionKeys.timesheet.read,
    children: [
      {
        index: true,
        element: <TimeTracker.component />,
        preload: TimeTracker.preload,
        scope: permissionKeys.timesheet.read,
      },
      {
        path: 'history',
        element: <TimesheetHistoryGuard.component />,
        preload: TimesheetHistoryGuard.preload,
        scope: permissionKeys.timesheet.read,
      },
    ],
  },
  {
    path: 'notes',
    element: <NotesPage.component />,
    preload: NotesPage.preload,
  },
  {
    // My calendar: Outlook for the interviews HR invites me to (2026-10-28).
    path: 'calendar-connection',
    element: <CalendarConnectionPage.component />,
    preload: CalendarConnectionPage.preload,
  },
  {
    // Candidates HR shared with me (2026-10-29). Every read is checked on
    // the server; the page shows its own empty and ended states.
    path: 'candidate-reviews',
    element: <CandidateReviewsPage.component />,
    preload: CandidateReviewsPage.preload,
  },
  {
    path: 'candidate-reviews/:shareId',
    element: <CandidateReviewsPage.component />,
    preload: CandidateReviewsPage.preload,
  },
  {
    path: 'tasks',
    element: <EmployeeTasksPage.component />,
    preload: EmployeeTasksPage.preload,
  },
  {
    path: '/surveys',
    element: <MySurveysPage.component />,
    preload: MySurveysPage.preload,
  },
  {
    path: '/signatures',
    element: <MyDocumentsToReadPage.component />,
    preload: MyDocumentsToReadPage.preload,
  },
  {
    path: '/signatures/read/:acknowledgementId',
    element: <ReadDocumentPage.component />,
    preload: ReadDocumentPage.preload,
  },
  {
    path: '/signatures/approvals/:requestId',
    element: <SignatureApprovalPage.component />,
    preload: SignatureApprovalPage.preload,
  },
  {
    path: '/signatures/answers/:requestId',
    element: <SignatureAnswersPage.component />,
    preload: SignatureAnswersPage.preload,
  },
  {
    path: '/trainings',
    element: (
      <TrainingGuard>
        <MyTrainingsPage.component />
      </TrainingGuard>
    ),
    preload: MyTrainingsPage.preload,
  },
  {
    path: '/trainings/:assignmentId',
    element: (
      <TrainingGuard>
        <TrainingPlayerPage.component />
      </TrainingGuard>
    ),
    preload: TrainingPlayerPage.preload,
  },
  {
    path: '/surveys/:id',
    element: <TakeSurveyPage.component />,
    preload: TakeSurveyPage.preload,
  },
  {
    path: '/performance/reviews',
    element: (
      <PerformanceGuard>
        <MyReviewsPage.component />
      </PerformanceGuard>
    ),
    preload: MyReviewsPage.preload,
  },
  {
    path: '/performance/reviews/submissions/:submissionId',
    element: (
      <PerformanceGuard>
        <WriteReviewPage.component />
      </PerformanceGuard>
    ),
    preload: WriteReviewPage.preload,
  },
  {
    path: '/performance/reviews/:reviewId',
    element: (
      <PerformanceGuard>
        <SharedReviewPage.component />
      </PerformanceGuard>
    ),
    preload: SharedReviewPage.preload,
  },
  {
    // Team feedback on a manager, once HR shared it (upward feedback U4).
    path: '/performance/team-feedback/:reviewId',
    element: (
      <PerformanceGuard>
        <TeamFeedbackPage.component />
      </PerformanceGuard>
    ),
    preload: TeamFeedbackPage.preload,
  },
  {
    // Not under My team's guard: HR may assign any active employee as the
    // manager reviewer (performance spec PF-2).
    path: '/performance/manager-reviews/:reviewId',
    element: (
      <PerformanceGuard>
        <ManagerReviewPage.component />
      </PerformanceGuard>
    ),
    preload: ManagerReviewPage.preload,
  },
  {
    path: '/performance/goals',
    element: (
      <PerformanceGuard>
        <GoalsPage.component />
      </PerformanceGuard>
    ),
    preload: GoalsPage.preload,
  },
  {
    path: '/performance/one-on-ones',
    element: (
      <PerformanceGuard>
        <OneOnOnesPage.component />
      </PerformanceGuard>
    ),
    preload: OneOnOnesPage.preload,
  },
  {
    path: '/performance/one-on-ones/:oneOnOneId',
    element: (
      <PerformanceGuard>
        <OneOnOneDetailPage.component />
      </PerformanceGuard>
    ),
    preload: OneOnOneDetailPage.preload,
  },
];

interface DefaultRoutesByPermission {
  path: string;
  scope: EmployeePermissionScope;
}

export const defaultRoutesByPermission: DefaultRoutesByPermission[] = [
  { path: '/dashboard', scope: permissionKeys.dashboard.read },
  {
    path: '/my-information/education',
    scope: permissionKeys.education.read,
  },
  { path: '/my-information/documents', scope: permissionKeys.document.read },
  { path: '/my-information/banking', scope: permissionKeys.bank_detail.read },
  { path: '/leave-management', scope: permissionKeys.leave.read },
  { path: '/my-information/family', scope: permissionKeys.family_detail.read },
];
